import { useEffect, useRef } from "react";
import { apiClient } from "../../../services/api";
import type { Api } from "../../../services/api";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventPageView } from "../../../tools/analytics/common.analytics";
import { gaEventViewItemPlaylist } from "../../../tools/analytics/domains/playlist.analytics";
import { useCongressAttributionFromNavigation } from "../../congress/congressAttribution";
import { useRecordPlaylistVisit } from "../stores/playlistVisits";
import type { ApiPlaylistModel } from "./useGetRecommendedPlaylistsQuery";

type ApiRoom = Api["get"]["/webapp/private/rooms/:id"]["output"]["body"];

/**
 * What opening a playlist page records, on mobile and desktop alike: the page
 * view and the playlist view in analytics, the view counter, and the entry of
 * the desktop search palette's "Recently viewed" for a signed-in reader.
 *
 * Recorded once per collection opened: a refetch (the window regaining focus)
 * brings a new object with the view counter this very call bumped, and must
 * not count another view. The page view names the room, so it waits for the
 * room query to settle. A room query that fails goes back to pending on each
 * refetch, so "settled" can flip: the latch below, not the dependencies, is
 * what keeps it to one view per collection.
 */
export function usePlaylistPageView({
  playlist,
  roomId,
  currentRoom,
  isRoomSettled,
  userId,
}: {
  playlist: ApiPlaylistModel | null;
  roomId: string | null;
  currentRoom: ApiRoom | null;
  /** False while the room query is in flight; true without a room. */
  isRoomSettled: boolean;
  userId: string | null;
}): void {
  // Stamped by the card that navigated here; null on every other entry.
  const congressAttribution = useCongressAttributionFromNavigation();

  const playlistId = playlist === null ? null : playlist.id;
  const recordedPlaylistId = useRef<string | null>(null);
  useEffect(() => {
    if (playlist === null || !isRoomSettled) return;
    if (recordedPlaylistId.current === playlist.id) return;
    recordedPlaylistId.current = playlist.id;
    gaEventPageView(currentRoom);
    gaEventViewItemPlaylist(
      { playlist, listName: AnalyticsItemList.PLAYLIST_PAGE },
      congressAttribution
    );
    // The reader has nothing to do about a failed count, so it gets no toast;
    // the rejection is left to the global handler rather than discarded.
    void apiClient.patch("/webapp/public/playlists/view/:playlistId", {
      param: { playlistId: playlist.id },
      json: roomId === null ? {} : { roomId },
    });
  }, [playlistId, isRoomSettled]);

  useRecordPlaylistVisit(
    playlist === null
      ? null
      : {
          id: playlist.id,
          title: playlist.title,
          articleCount: playlist.metrics.articles,
          roomId,
        },
    userId
  );
}
